import { createContext, ReactNode, useEffect, useState } from "react";
import {
  CrmType,
  UserSettings,
  AutoReplyBotSettings,
  FirestoreContextProps
} from "../@types/firestore";
import useAuth from "../hooks/useAuth";
import { parsePhoneNumber, toInternationalFormat } from "utils/Haim/phoneNumbersUtils";
import { useLocation } from "react-router";
import { db } from "firebaseConfig";
import { getCountFromServer, query, collection } from "firebase/firestore";
import { removeUndefined } from "utils/Haim/firestoreUtils";
import { randomNumber } from "utils/Haim/utils";
import { checkListingAliasAvailability } from "utils/listings/query";
import { organizationConverter } from "../@types/listings/organization";
import ReactPixel from "react-facebook-pixel";
import { GenericConverter } from "utils/firestoreConverters";
import type { AdditionalUser, User } from "../@types/listings/user";
import type { Organization } from "../@types/listings/organization";
import type { ListingData } from "../@types/listings/new";
import type { BulkMessageSentMessage } from "../@types/sharedSchema";

const FirestoreContext = createContext({} as FirestoreContextProps);

type FirestoreProviderProps = {
  children: ReactNode;
};

function FirestoreProvider({ children }: FirestoreProviderProps) {
  const { user, isAuthenticated } = useAuth();

  const [myVirtualNumbers, setMyVirtualNumbers] = useState<Array<string>>([]);
  const [myApiToken, setMyApiToken] = useState<any>(null);

  const [callsByVirtualNumber, setCallsByVirtualNumber] = useState<Map<string, any>>(new Map());
  const [shouldFetchAllCallsByVirtualNumber, setShouldFetchAllCallsByVirtualNumber] =
    useState<boolean>(false);
  const [featureFlags, setFeatureFlags] = useState<any>({});
  const [userSettings, setUserSettings] = useState<UserSettings & { email: string }>(null);
  const [autoReplyBotSettings, setAutoReplyBotSettings] = useState<AutoReplyBotSettings>(null);
  const { search } = useLocation();
  const [messagesSentCount, setMessagesSentCount] = useState(0);
  const [listingData, setListingData] = useState<null | ListingData>(null);
  // Starts true on purpose: it used to start false, which made
  // renderDashboardMainPage render DashboardMain → LinearProgress → DashboardMain
  // on every load (the user-doc effect flipped it false→true→false), unmounting
  // and remounting the whole main page and every listener its subtree opens.
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isLoadingListingData, setIsLoadingListingData] = useState(false);

  const [organization, setOrganization] = useState<null | Organization>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationUsers, setOrganizationUsers] = useState<AdditionalUser[]>([]);
  const [isLoadingOrganization, setIsLoadingOrganization] = useState(true);

  const isAdminOfTheOrganization = organization?.admins?.includes(user.email);

  const organizationId = organization?.id;

  const isRealEstateUser = userSettings?.crmType === "realEstate";

  useEffect(() => {
    if (!user || !isAuthenticated || !user.emailVerified) {
      // Unverified email/password users are blocked at the verify-email gate and
      // the Firestore rules deny them anyway — don't subscribe (avoids a
      // permission-denied error) until the email is verified. isLoadingUser
      // deliberately stays true here: only auth-guarded routes read it, and
      // setting it false pre-auth would re-create the mount→unmount→remount
      // cycle this flag's initial value was fixed to prevent.
      return;
    }

    console.log("Fetching dashboard settings for user", user.email);

    setIsLoadingUser(true);
    return db
      .collection("dashboard-users-v2")
      .doc(user.email)
      .withConverter(GenericConverter<UserSettings>())
      .onSnapshot(
        (snapshot) => {
          if (!snapshot.exists) {
            console.log("Got no results for user:", user.email);
            return;
          }

          const data = snapshot.data() as UserSettings;

          setUserSettings({ ...data, email: user.email });

          const { virtualNumbers, apiToken, featureFlags } = snapshot.data() as any;

          setMyVirtualNumbers(virtualNumbers || []);
          setMyApiToken(apiToken || "");
          setFeatureFlags(featureFlags || {});
          setIsLoadingUser(false);
        },
        () => {
          setIsLoadingUser(false);
        }
      );
  }, [user, isAuthenticated]);

  useEffect(() => {
    if (!user || !userSettings) return;

    if (userSettings.crmType !== CrmType.realEstate) return;

    setIsLoadingListingData(true);

    const backfillListingAliases = async (): Promise<void> => {
      const normalizedAlias = user.displayName.split(" ").join("").toLowerCase();

      let available = false;

      let newListingAlias = normalizedAlias;

      const firstGeneratedAliasAvailable = await checkListingAliasAvailability(newListingAlias);

      if (firstGeneratedAliasAvailable) available = true;

      while (!available) {
        newListingAlias = normalizedAlias + randomNumber(1000, 9999);
        const newGeneratedAliasAvailable = await checkListingAliasAvailability(newListingAlias);

        if (newGeneratedAliasAvailable) available = true;
      }

      const { ownerFullName, email, photoURL, ownerPhoneNumber } = userSettings;

      const newUserSettings: Omit<User, "id"> = {
        name: ownerFullName,
        email: email,
        avatarUrl: photoURL,
        listingBio: "",
        phoneNumber: ownerPhoneNumber,
        listingAlias: newListingAlias,
        coverAvatarUrl: null,
        coverAvatarColor: null
      };

      void updateUserListingData(newUserSettings);
    };

    const listingDataSnapshot = db
      .collection("dashboard-users-v2")
      .doc(user.email)
      .collection("listingData")
      .doc(user.email)
      .onSnapshot(
        (snapshot) => {
          if (!snapshot.exists) {
            void backfillListingAliases();
            return;
          }

          const newListingData = snapshot.data() as ListingData;

          setListingData(newListingData);

          setIsLoadingListingData(false);
        },
        () => setIsLoadingListingData(false)
      );

    return listingDataSnapshot;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userSettings]);

  useEffect(() => {
    if (!user || !isAuthenticated) {
      return;
    }

    console.log("Fetching auto reply bot settings for user", user.email);

    return db
      .collection("official-whatsapp-bots")
      .where("ownerMail", "==", user.email)
      .onSnapshot((snapshot) => {
        if (snapshot.empty) {
          console.log("Got no results for user:", user.email);
          return;
        }

        const data = snapshot.docs[0].data() as AutoReplyBotSettings;

        setAutoReplyBotSettings(data);
      });
  }, [user, isAuthenticated]);

  useEffect(() => {
    if (!user || !isRealEstateUser) return;

    const getOrganization = async (): Promise<void> => {
      setIsLoadingOrganization(true);

      try {
        const organizationsSnapshot = await db
          .collection("dashboard-orgs")
          .withConverter(organizationConverter)
          .where("users", "array-contains", user.email)
          .get();

        if (organizationsSnapshot.empty) {
          setIsLoadingOrganization(false);
          return;
        }

        const organizationsData = organizationsSnapshot.docs.map((doc) => doc.data());
        const organizationData = organizationsData[0];

        setOrganization(organizationData);
        setOrganizations(organizationsData);

        setIsLoadingOrganization(false);
      } catch (error) {
        console.error(error);
        setIsLoadingOrganization(false);
      }
    };

    void getOrganization();
  }, [user, isRealEstateUser]);

  useEffect(() => {
    if (!user || !isRealEstateUser) return;

    if (!organization) return;

    const orgsUsers = organization.users;

    if (!orgsUsers.length) return;

    const getOrganizationUsers = async (): Promise<void> => {
      try {
        const userRefsSnapshot = await Promise.all(
          orgsUsers.map((email) =>
            db.collectionGroup("listingData").where("email", "==", email).get()
          )
        );

        const newOrganizationUsers = userRefsSnapshot.map(({ empty, docs }, index) =>
          empty ? { email: orgsUsers[index] } : (docs[0].data() as User)
        );

        setOrganizationUsers(newOrganizationUsers);
      } catch (error) {
        console.error(error);
      }
    };

    void getOrganizationUsers();
  }, [user, organization, isRealEstateUser]);

  // backfilling user settings, adding photo url from google if missing
  useEffect(() => {
    if (!user || !userSettings) {
      return;
    }

    let newUserSettings: Partial<UserSettings> = {};

    if (!userSettings.photoURL && user.photoURL) {
      newUserSettings.photoURL = user.photoURL;
    }

    if (!userSettings.ownerFullName && user.displayName) {
      newUserSettings.ownerFullName = user.displayName;
    }

    if (!userSettings.crmType) {
      console.warn("No CRM type defined. Going with real estate");
      newUserSettings.crmType = CrmType.realEstate;
    }

    if (Object.keys(newUserSettings).length) {
      updateUserSettings(newUserSettings);
    }
  }, [user, userSettings]);

  useEffect(() => {
    const checkAuthStatus = async () => {
      // Only provision a user doc for a verified account. An unverified
      // email/password user must not be able to self-create their record — the
      // Firestore rules reject the write, and provisioning here would just
      // surface a permission error. Google users are always verified.
      if (isAuthenticated && user && user.emailVerified) {
        console.log("User auth changed, checking if it is a new user", user.email);

        const userResult = await db.collection("dashboard-users-v2").doc(user.email).get();

        if (!userResult.exists) {
          console.log("Got no results for user, he is probably a new user:", user.email);
          const wantedCrmType = new URLSearchParams(search).get("crmType") ?? CrmType.realEstate;

          let result = await db
            .collection("dashboard-users-v2")
            .doc(user.email)
            .set({
              apiToken: Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2),
              virtualNumbers: [],
              crmType: wantedCrmType
            });

          try {
            ReactPixel?.track("Lead", { email: user.email });
          } catch (error) {
            console.error("Error in ReactPixel", error);
          }
          console.log("Created new data for user:", user.email, result);
        }
      }
    };
    checkAuthStatus();
  }, [user, isAuthenticated]);

  useEffect(() => {
    if (!user || !isAuthenticated) {
      return;
    }

    console.log("Fetching sent-messages count for user: ", user.email);

    // Aggregation count instead of the old `.limit(110)` listener, which
    // downloaded up to 110 full message docs just to read `docs.size`. The
    // free-trial gate only needs the number; sends made in this session bump
    // the count locally in saveSentMessage below.
    let cancelled = false;
    void getCountFromServer(
      query(collection(db, "dashboard-users-v2", user.email, "sentMessages"))
    )
      .then((snapshot) => {
        if (!cancelled) setMessagesSentCount(snapshot.data().count);
      })
      .catch((error) => console.error("Failed counting sentMessages:", error));

    return () => {
      cancelled = true;
    };
  }, [user, isAuthenticated]);

  // NOTE: a `callers` listener (payCall-settings/{vn}/callers, unbounded) used
  // to live here. It was pure dead weight — its state was never exposed in the
  // provider value and had zero readers — while downloading every caller doc
  // for every virtual number on every page load (2,500+ docs for a heavy
  // account). Removed 2026-08-20; NewLeadsTable opens its own listener.

  useEffect(() => {
    if (
      !db ||
      !user ||
      !isAuthenticated ||
      !myVirtualNumbers ||
      myVirtualNumbers.length === 0 ||
      !shouldFetchAllCallsByVirtualNumber
    ) {
      return;
    }

    const snapshotCancellations = [];

    for (const virtualNumber of myVirtualNumbers) {
      console.log("Fetching calls for virtual number: ", virtualNumber);

      const snapshotCancellation = db
        .collection("payCall-settings")
        .doc(virtualNumber)
        .collection("calls-summary")
        // The only consumer is the dashboard's LatestCallers widget (top 10
        // across all virtual numbers). This listener used to be unbounded and
        // downloaded a heavy account's entire call history (measured: 15,506
        // docs for one customer) to render 10 rows. 25 per number leaves the
        // client-side merge+sort (by endAt) plenty of margin over the
        // createdTime query order.
        .orderBy("createdTime", "desc")
        .limit(25)
        .onSnapshot((snapshot) => {
          console.log("Firebase calls summary snapshot for virtual number", virtualNumber);

          const entries: Array<any> = snapshot.docs
            .filter((doc) => doc.id != null)
            .map((doc) => {
              const data = doc.data();
              data._metadata = data.metadata ?? {};
              data._metadata.callerPhoneNumber = parsePhoneNumber(data.callerNumber);
              data._metadata.firestorePath = doc.ref.path;
              return data;
            });
          setCallsByVirtualNumber((oldValue) => {
            var newValue = new Map(oldValue);
            newValue.set(virtualNumber, entries);
            return newValue;
          });
        });
      snapshotCancellations.push(snapshotCancellation);
    }

    return () => {
      console.log("Running snapshot cancellations...");
      snapshotCancellations.forEach((func) => func());
    };
  }, [user, isAuthenticated, myVirtualNumbers, shouldFetchAllCallsByVirtualNumber]);

  /**
   * The single door every `ownerPhoneNumber` write goes through (settings
   * profile card, onboarding, the signature "missing profile fields" dialog,
   * the creatives onboarding), so the normalization lives here rather than in
   * each caller — a new caller is then correct by default.
   *
   * `ownerPhoneNumber` is documented as international format, and the server
   * RELIES on that: `getUsersByOwnerPhoneNumber` resolves a phone with Firestore
   * EQUALITY queries on exactly three shapes of the inbound E.164 (`+972XXXXXXXXX`,
   * `972XXXXXXXXX`, `0XXXXXXXXX`). A stored value that is none of those
   * byte-for-byte is unfindable, and the failure is silent — measured 2026-09-29,
   * a paying customer stored as `"+972 54 447 0407"` (a free-text dialog wrote it
   * with only `.trim()`) could not be recognised by the WhatsApp AI assistant's
   * gate and was answered with the sales pitch instead.
   *
   * Only ever REFORMATS: if the value can't be parsed we keep what the user
   * typed rather than dropping it, and let the field's own validation complain.
   */
  const updateUserSettings = async (data: Partial<UserSettings>) => {
    const payload =
      typeof data?.ownerPhoneNumber === "string" && data.ownerPhoneNumber.trim()
        ? {
            ...data,
            ownerPhoneNumber:
              toInternationalFormat(data.ownerPhoneNumber) ?? data.ownerPhoneNumber.trim()
          }
        : data;
    console.log(`Updating user settings for ${user.email} with:`, payload);
    await db.collection("dashboard-users-v2").doc(user.email).set(payload, { merge: true });
  };

  const updateUserListingData = async (data: Partial<User>): Promise<void> => {
    await db
      .collection("dashboard-users-v2")
      .doc(user.email)
      .collection("listingData")
      .doc(user.email)
      .set(data, { merge: true });
  };

  const saveSentMessage = async (messageObject: BulkMessageSentMessage) => {
    removeUndefined(messageObject);

    console.log("Saving message for ", user, messageObject);

    await db
      .collection("dashboard-users-v2")
      .doc(user.email)
      .collection("sentMessages")
      .doc()
      .set(messageObject);

    // Keep the free-trial gate live without a listener (see the count effect).
    setMessagesSentCount((count) => count + 1);
  };

  const changeOrganization = (organizationId: string) => {
    const newOrganization = organizations.find((org) => org.id === organizationId);

    if (!newOrganization) return;

    setOrganization(newOrganization);
  };

  return (
    <FirestoreContext.Provider
      value={{
        userSettings,
        autoReplyBotSettings,
        myVirtualNumbers,
        callsByVirtualNumber,
        setShouldFetchAllCallsByVirtualNumber,
        myApiToken,
        featureFlags,
        updateUserSettings,
        updateUserListingData,
        saveSentMessage,
        messagesSentCount,
        listingData,
        isLoadingUser,
        isLoadingListingData,
        organization,
        organizations,
        organizationId,
        organizationUsers,
        isLoadingOrganization,
        isAdminOfTheOrganization,
        changeOrganization
      }}
    >
      {children}
    </FirestoreContext.Provider>
  );
}

export { FirestoreProvider, FirestoreContext };
